import { useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { Router } from '@/app/providers/router';
import * as Styled from '@/app/styles/app-layouts';
import * as COLOR from '@/app/theme/yacht/colors';
import { useIsProxyLogin, useUser } from '@/entities/user';
import { useUserInitQuery } from '@/entities/user/api/user.api';
import { useAgreementsModal } from '@/features/agreements';
import { AppRoutes } from '@/shared/constants/app-routes.constant';
import { useMatchMedia } from '@/shared/lib/hooks/use-match-media.hook';
import { Header } from '@/widgets/header';
import { Sidebar } from '@/widgets/sidebar';

export const NOT_ACTIVE = 'Not Active';

const HtmlContentComponent = () => {
	const isTablet = useMatchMedia('tabletAndMobile');
	const { acceptedLatestAgreements } = useUser();
	const isProxyLogin = useIsProxyLogin();
	const { refetch } = useUserInitQuery(null);
	const [openConditionsAndPrivacyPolicyModal, close] = useAgreementsModal();
	const { pathname } = useLocation();
	const { isDark } = useTheme();

	useEffect(() => {
		const zendeskIframe = document.querySelector("iframe[id*='launcher']");

		if (zendeskIframe) {
			zendeskIframe.classList.add('authenticated');
		}
	}, []);

	useEffect(() => {
		const updateIframeSize = () => {
			const zendeskIframe = document.querySelector("iframe[id*='launcher']") as HTMLIFrameElement;

			if (!zendeskIframe) return;

			const iframeDocument = zendeskIframe.contentDocument || zendeskIframe.contentWindow?.document;

			if (iframeDocument) {
				const launcherElement = iframeDocument.querySelector('[data-testid="launcher"]') as HTMLElement;

				if (launcherElement) {
					zendeskIframe.style.width = `${launcherElement.offsetWidth}px`;
				}
			}
		};

		updateIframeSize();

		window.addEventListener('resize', updateIframeSize);

		return () => window.removeEventListener('resize', updateIframeSize);
	}, []);

	useEffect(() => {
		const zE = (window as { zE?: (action: string, command: string, payload?: unknown) => void }).zE;

		if (zE) {
			zE('webWidget', 'updateSettings', {
				color: isDark
					? {
							theme: COLOR.DARK_4,
							launcher: COLOR.DARK_4,
							button: COLOR.DARK_4,
					  }
					: {
							theme: COLOR.BLUE_1,
							launcher: COLOR.BLUE_1,
							button: COLOR.BLUE_1,
					  },
			});
		}
	}, [isDark]);

	useLayoutEffect(() => {
		if (pathname === AppRoutes.TermsOfService || pathname === AppRoutes.Privacy) {
			return;
		} else if (isProxyLogin) {
			return;
		} else if (!isProxyLogin && !acceptedLatestAgreements) {
			close();

			openConditionsAndPrivacyPolicyModal({ isOverlayClose: false });
		}
	}, [pathname, acceptedLatestAgreements, isProxyLogin]);

	useEffect(() => {
		const intervalId = setInterval(() => {
			refetch();
		}, 3 * 60 * 60 * 1000);

		return () => clearInterval(intervalId);
	}, [refetch]);

	return (
		<>
			{!isTablet && <Sidebar />}
			<Styled.ContentWrapper>
				<Header />
				<Router />
			</Styled.ContentWrapper>
		</>
	);
};

export default HtmlContentComponent;
